/**
 * The funnel's front door (Monetization v2, sc-6940; spend-timing fix
 * sc-7036).
 *
 * Every INSERT COIN routes through resolvePlayAccess() BEFORE a game
 * mounts. It CHECKS eligibility and returns a verdict — it does NOT spend
 * the token. The token is spent later, at run START (play.ts onStarted),
 * so a game that fails to load (500, CDN blip, back-out from the start
 * screen) costs the player nothing (sc-7036). The `access` field tells
 * the caller what to do at start: 'token' ⇒ spend one; 'pass' ⇒ nothing;
 * 'free-taste' ⇒ already consumed here (anon, atomic, single-shot).
 *
 * The five branches:
 *   entitled (active pass)          → 'play' access:'pass'        (no spend ever)
 *   signed-in + tokens remaining    → 'play' access:'token'       (spend at START)
 *   signed-in + exhausted           → 'paywall'                   (stub until sc-6944)
 *   anon + free taste unused        → 'play' access:'free-taste'  (consumed HERE, atomically)
 *   anon + free taste used          → 'login'                     (the wall)
 *
 * The anon taste is still consumed at the gate (it's atomic + single-shot
 * server-side, and an anon "run" has no separate paid resource to protect
 * on failure). Only the signed-in TOKEN spend is deferred to start.
 *
 * Fail-CLOSED: if the wallet read (or taste consume) throws, we do NOT
 * hand out a free run — signed-in players see the paywall/retry, anon
 * get the login wall.
 */

import { consumeTaste, getWallet } from '../api/client.js';
import { isSignedIn } from '@arcraz/arcadia-auth-web';

export type PlayVerdict =
  | { grant: 'play'; access: 'pass' | 'token' | 'free-taste' }
  | { grant: 'login' }
  | { grant: 'paywall'; resetsAtUtc?: string }
  | { grant: 'error' };

export async function resolvePlayAccess(): Promise<PlayVerdict> {
  if (!isSignedIn()) {
    // Anonymous: consume the daily free taste ATOMICALLY server-side
    // (sc-6940). Reading a client-cached meter raced — two rapid
    // launches both played free. The server is the single authority.
    // A throw (503 fail-closed / network) → login wall, never a free run.
    try {
      const taste = await consumeTaste();
      return taste.allowed
        ? { grant: 'play', access: 'free-taste' }
        : { grant: 'login' };
    } catch {
      return { grant: 'login' };
    }
  }

  // Signed in: the wallet CHECKS eligibility — a pass is unlimited, else
  // the player must have tokens left. The token is NOT spent here; it's
  // spent at run start (sc-7036) so a failed game load costs nothing.
  try {
    const wallet = await getWallet();
    if (wallet.entitlement) {
      return { grant: 'play', access: 'pass' };
    }
    if (wallet.tokensRemaining > 0) {
      return { grant: 'play', access: 'token' };
    }
    // No tokens and no pass ⇒ exhausted.
    return { grant: 'paywall', resetsAtUtc: wallet.resetsAtUtc };
  } catch {
    // Wallet read threw (incl. the 503 fail-closed path). Never a free
    // run for a signed-in player — send them to the paywall/retry.
    return { grant: 'error' };
  }
}
